import { graphql } from 'gatsby';

import { Grid2 as Grid, styled } from '@mui/material';

import { MarkdownAccordion, ResponsiveGrid } from '@mewa/ui';

import { markdownToHTML } from '../../../../libs/markdown-to-html';
import { PossibleContentSections } from '../fragment-and-types';
import { ContentLivePreviewConfig } from '../live-preview/types';
import { MarkdownContentWithImage } from './markdown-content-with-image-description';

export const MarkdownAccordionFragment = graphql`
  fragment MarkdownAccordionFragment on STRAPI__COMPONENT_SHARED_ACCORDION {
    accordionItems: AccordionItem {
      title: Title
      html: Html {
        data {
          childMarkdownRemark {
            html
          }
        }
      }
    }
    strapi_component
  }
`;

export const componentName = 'shared.accordion';

type AccordionItem = {
  title: string;
  html: {
    data: {
      childMarkdownRemark: { html: string };
    };
  };
};

export type AccordionData = {
  accordionItems: AccordionItem[];
  strapi_component: typeof componentName;
};

export type AccordionPreview = {
  strapi_component: typeof componentName;
  AccordionItem: {
    Title: string;
    Html: string;
  }[];
};

export const isMarkdownAccordion = (
  component: PossibleContentSections,
): component is AccordionData => {
  return component.strapi_component === componentName;
};

export const StrapiMarkdownAccordion = (data: AccordionData) => {
  return (
    <Wrapper>
      <Grid
        offset={{ xs: 0, md: 1 }}
        size={{
          xs: 12,
          md: 10,
        }}
      >
        {data.accordionItems.map((item, index) => (
          <MarkdownAccordion title={item.title}>
            <StyledMarkdownContentWithImage
              html={item.html.data.childMarkdownRemark.html}
            />
          </MarkdownAccordion>
        ))}
      </Grid>
    </Wrapper>
  );
};

const Wrapper = styled(ResponsiveGrid)({
  marginBottom: '50px',
});

export const config = {
  isComponent: isMarkdownAccordion,
  Component: StrapiMarkdownAccordion,
  componentName,
};

export const previewConfig: ContentLivePreviewConfig = {
  populationParams: { AccordionItem: '*' },
  componentName,
  mapProperties: (accordion): AccordionData | null =>
    accordion.strapi_component === componentName
      ? {
          ...accordion,
          accordionItems: (accordion as AccordionPreview).AccordionItem.map(
            (item) => ({
              title: item.Title,
              html: markdownToHTML(item.Html),
            }),
          ),
        }
      : null,
};

const StyledMarkdownContentWithImage = styled(MarkdownContentWithImage)(
  ({ theme }) => ({
    p: {
      '&:first-child': { marginTop: 0 },
      '&:last-child': { marginBottom: 0 },
    },

    marginBottom: 0,

    [theme.breakpoints.up('sm')]: {
      marginBottom: 0,
    },
  }),
);
